import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SITE } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rc_admin_token";

const STATUSES = [
  { key: "new", label: "New", active: "bg-amber-500 text-white border-amber-500" },
  { key: "contacted", label: "Contacted", active: "bg-ocean text-white border-ocean" },
  { key: "booked", label: "Booked", active: "bg-seafoam text-white border-seafoam" },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState(null);

  const loadQuotes = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/quotes`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setQuotes(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (token) loadQuotes(token);
  }, [token, loadQuotes]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setQuotes(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/quotes/${id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
    } catch {
      toast.error("Couldn't update the status. Please try again.");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mist px-6" data-testid="admin-login-page">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl bg-white p-10 shadow-[0_40px_100px_-40px_rgba(14,53,39,0.35)]"
        >
          <img src="/logo.png" alt={`${SITE.name} logo`} className="h-14 w-auto" />
          <h1 className="mt-6 font-serif text-3xl tracking-tight text-ink">Owner Login</h1>
          <p className="mt-2 text-sm text-slateblue">Private area — quote requests live here.</p>
          <div className="mt-8">
            <Label htmlFor="a-email" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Email</Label>
            <Input
              id="a-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 h-12 rounded-lg border-ink/15 text-base focus-visible:ring-seafoam"
            />
          </div>
          <div className="mt-5">
            <Label htmlFor="a-pass" className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">Password</Label>
            <Input
              id="a-pass"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 rounded-lg border-ink/15 text-base focus-visible:ring-seafoam"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-sm font-medium text-red-600">{error}</p>
          )}
          <Button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="mt-8 h-12 w-full rounded-full bg-ocean text-sm font-bold uppercase tracking-[0.2em] text-salt transition-[background-color] duration-300 hover:bg-seafoam"
          >
            {loading ? "Signing in…" : "Sign In"}
          </Button>
          <a href="/" data-testid="admin-back-home-link" className="mt-6 block text-center text-sm text-slateblue underline underline-offset-4 hover:text-ocean">
            Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-salt" data-testid="admin-dashboard">
      <header className="border-b border-ink/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="/" data-testid="admin-logo-link">
            <img src="/logo.png" alt={`${SITE.name} logo`} className="h-10 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              data-testid="admin-refresh-button"
              onClick={() => loadQuotes(token)}
              className="rounded-full border-ink/15"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button
              variant="outline"
              data-testid="admin-logout-button"
              onClick={logout}
              className="rounded-full border-ink/15"
            >
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="font-serif text-4xl tracking-tight text-ink">Quote Requests</h1>
        <p className="mt-2 text-sm text-slateblue" data-testid="admin-quote-count">
          {quotes ? `${quotes.length} request${quotes.length === 1 ? "" : "s"} — newest first` : "Loading…"}
        </p>

        {quotes && quotes.length === 0 && (
          <div className="mt-16 flex flex-col items-center text-center" data-testid="admin-empty-state">
            <Inbox className="h-12 w-12 text-seafoam" strokeWidth={1.5} />
            <p className="mt-4 font-serif text-2xl text-ink">No requests yet</p>
            <p className="mt-2 text-sm text-slateblue">New quote requests will appear here the moment they arrive.</p>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {quotes?.map((q, i) => (
            <article
              key={q.id}
              data-testid={`admin-quote-card-${i}`}
              className="rounded-2xl border border-ink/10 bg-white p-6 transition-[box-shadow] duration-300 hover:shadow-[0_20px_50px_-24px_rgba(14,53,39,0.25)]"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-serif text-xl tracking-tight text-ink">{q.name}</h2>
                  <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.2em] text-seafoam">
                    {q.service_type} · {q.frequency}
                  </p>
                </div>
                <span className="text-xs text-slateblue/70">{fmtDate(q.created_at)}</span>
              </div>
              <dl className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div><dt className="font-semibold text-ink">Phone</dt><dd className="text-slateblue">{q.phone}</dd></div>
                <div><dt className="font-semibold text-ink">Email</dt><dd className="text-slateblue">{q.email}</dd></div>
                <div><dt className="font-semibold text-ink">Area</dt><dd className="text-slateblue">{q.service_area || "—"}</dd></div>
                <div><dt className="font-semibold text-ink">Size</dt><dd className="text-slateblue">{q.size || "—"}</dd></div>
                <div><dt className="font-semibold text-ink">Preferred</dt><dd className="text-slateblue">{q.preferred_date || "—"}</dd></div>
                {q.notes && (
                  <div className="sm:col-span-2 lg:col-span-3"><dt className="font-semibold text-ink">Notes</dt><dd className="text-slateblue">{q.notes}</dd></div>
                )}
              </dl>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <div className="flex rounded-full border border-ink/15 p-0.5" role="group" aria-label="Lead status">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`admin-status-${s.key}-${i}`}
                      onClick={() => setStatus(q.id, s.key)}
                      aria-pressed={(q.status || "new") === s.key}
                      className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-[background-color,color] duration-200 ${
                        (q.status || "new") === s.key
                          ? s.active
                          : "border-transparent text-slateblue hover:text-ink"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <a
                  href={`tel:${q.phone}`}
                  data-testid={`admin-quote-call-${i}`}
                  className="rounded-full bg-ocean px-4 py-2 text-xs font-bold uppercase tracking-widest text-salt transition-[background-color] duration-300 hover:bg-seafoam"
                >
                  Call
                </a>
                <a
                  href={`mailto:${q.email}?subject=Your Rock Coast Cleaning quote`}
                  data-testid={`admin-quote-email-${i}`}
                  className="rounded-full border border-ink/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-ink transition-[background-color] duration-300 hover:bg-mist"
                >
                  Email
                </a>
              </div>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
